import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText, {getLocale} from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import Plugin from './models/Plugin';
import Organization from './models/Organization';
import uris from '~/uris';
import Pagination from './Pagination';
import {transformKeyToVar} from '~/utils/tools';
import _ from 'lodash';

class PluginStore extends Pagination {
  @observable plugins = [];

  @observable currentPlugin = {};

  @observable isLoading = false;

  @observable assignedOUList = [];

  @observable organizationAssignedPlugins = [];

  @observable currentPluginId = '';

  @observable pluginsLength;

  @action
  setPluginsLength(num) {
    this.pluginsLength = num;
  }

  @action resetLoading() {
    this.isLoading = false;
  }

  @action setPlugins(data) {
    this.plugins = data;
  }

  @action
  changeCurrentPlugin(data) {
    this.currentPlugin = data;
  }

  @action.bound
  changePagination(pagination) {
    super.changePagination(pagination);
    this.getPluginsWithAddition();
  }

  @action.bound
  getOrganizationPluginList() {
    return axiosInstance.get(uris.getOrganizationPluginList()).then((data) => {
      this.setOrganizationAssignedPlugins(data.plugins || []);
    });
  }

  @action
  async getPlugins(body = {}) {
    this.isLoading = true;
    await axiosInstance
      .post(uris.getPluginList(), {
        ...body
      })
      .then((data) => {
        this.setPagination(data.pagination);
        this.setPlugins(data.plugins.map(json => this.updatePluginFromServer(json)));
        this.changeCurrentPlugin((data.plugins || [])[0]);
        this.setPluginsLength((data.plugins || []).length);
        this.resetLoading();
      })
      .catch(() => this.resetLoading());
  }

  @action.bound
  getPluginsWithAddition(body = {}) {
    let data = {
      ...this.paginationRequestBody
    };

    if (this.pluginsLength === 1 && body.isDelete) {
      data = {
        ...this.paginationRequestBodyBefore
      };
    }

    if (body.forceFresh) {
      data = {
        ...this.initPaginationRequestBody
      };
    }

    return this.getPlugins({
      ...data,
      ...body
    });
  }

  @action.bound
  registerPlugin(data) {
    return axiosInstance
      .post(
        uris.createPlugin(),
        {
          nameJson: _.merge(transformKeyToVar(data.name), {default: data.name[getLocale()]}),
          url: 'https://'.concat(data.url.trim()),
          descriptionJson: _.merge(transformKeyToVar(data.description), {default: (data.description && data.description[getLocale()]) || '-'})
        }
      )
      .then(() => {
        message.success(localeText('PLUGIN.MESSAGE_REGISTER_SUCCEED'));
        this.getPluginsWithAddition({forceFresh: true});
      })
      .catch(() => {
        this.resetLoading();
        return Promise.reject();
      });
  }

  @action.bound
  modifyPlugin(data) {
    this.isLoading = true;
    return axiosInstance
      .post(
        uris.modifyPlugin(),
        {
          id: this.currentPlugin.id,
          nameJson: _.merge(transformKeyToVar(data.nameModify), {default: data.nameModify[getLocale()]}),
          descriptionJson: _.merge(transformKeyToVar(data.descriptionModify), {default: data.descriptionModify[getLocale()] || '-'}),
          url: 'https://'.concat(data.urlModify.trim())
        }
      )
      .then((json) => {
        this.updatePluginFromServer(json);
        this.resetLoading();
        message.success(localeText('PLUGIN.MESSAGE_MODIFY_SUCCEED'));
      })
      .catch(() => {
        this.resetLoading();
        return Promise.reject();
      });
  }

  @action.bound
  deletePlugin(record) {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.deletePlugin(),
        {
          id: record.id
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_DELETE_FAIL'),
            content: localeText('PLUGIN.CONTENT_MODAL_DELETE_PLUGIN_FAILED1')
          }
        }
      )
      .then(() => {
        message.success(localeText('PLUGIN.MESSAGE_DELETE_SUCCESS'));
        this.getPluginsWithAddition({isDelete: true});
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  checkPluginExist(name, cb, id) {
    axiosInstance
      .post(uris.checkNameExist(), null, {
        params: {type: 'plugin', name, id},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error);
      });
  }

  @action.bound
  assignPluginToOU(pluginId, organizationIds) {
    this.isLoading = true;
    return axiosInstance
      .post(uris.assignPluginToOrganization(), {
        pluginId,
        organizationIds
      })
      .then(() => {
        this.resetLoading();
        message.success(localeText('PLUGIN.MESSAGE_ASSIGN_OU'));
      })
      .catch(() => {
        this.resetLoading();
        return Promise.reject();
      });
  }

  @action
  getAssignedOUList(pluginId) {
    this.isLoading = true;
    return axiosInstance
      .post(uris.getAssignedOUList(), null, {
        params: {pluginId}
      })
      .then((data) => {
        this.setAssignedOUList((data.organizations || []).map(json => this.updateAssignedOUListFromServer(json)));
        this.resetLoading();
        return data;
      })
      .catch(() => this.resetLoading());
  }

  @action
  setAssignedOUList(data) {
    this.assignedOUList = data;
  }

  @action
  updateAssignedOUListFromServer(json) {
    let ou = (this.assignedOUList || []).find(({id}) => id === json.id);
    if (!ou) {
      ou = new Organization(this, json.id);
    }
    ou.updateFromJson(json);
    return ou;
  }

  @action.bound
  updatePluginFromServer(json) {
    let plugin = this.plugins.find(({id}) => id === json.id);
    if (!plugin) {
      plugin = new Plugin(this, json.id);
    }
    plugin.updateFromJson(json);
    return plugin;
  }

  @action
  updateOuFromServer(json) {
    const ou = new Organization(this, json.id);
    ou.updateFromJson(json);
    return ou;
  }

  @action.bound
  setOrganizationAssignedPlugins(plugins) {
    this.organizationAssignedPlugins = plugins;
  }

  @action.bound
  setCurrentPluginId(id) {
    this.currentPluginId = id;
  }
}

export default new PluginStore();
